import {css} from '@mui/material';
import {TooltipWrapperCore} from '@wallaby-ui/core';
import React from 'react';
import {useIntl} from 'react-intl';

import {AlertEpisodeDisplayedState, AlertEpisodeTooltipDto} from './alert_types';
import {AlertCriteriaRenderer, AlertGenericCriteriaChips} from './AlertCriteriaChips';
import {useFormatters} from '../LightFormatter';

type Props = {
  tooltip: AlertEpisodeTooltipDto;
  displayedState: AlertEpisodeDisplayedState;
  /** Rendered as the actor when the BE sends none (a system activation or deactivation). */
  systemActorName: string;
  /** Product renderer of the criteria chips (the list pages' own chip builders); the generic chips otherwise. */
  renderCriteria?: AlertCriteriaRenderer;
  children: React.ReactNode;
};

/* the white card of the design (`.crittip`): 290 wide, header, criteria chips, footer with the last change */
const popperCss = css`
  width: 290px;
  max-width: 290px;
  background-color: #fff;
  color: var(--dlThemeGray-800);
  border: 1px solid var(--dlThemeGray-100);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  padding: 13px 15px;
  line-height: normal;
  text-align: left;
`;
const wrapperCss = css`
  display: inline-block;
  max-width: 100%;
`;
const headerCss = css`
  display: block;
  font-family: var(--dlFontFamilySemiBold);
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--dlTheme-600);
  margin-bottom: 9px;
`;
const footerCss = css`
  display: block;
  margin-top: 11px;
  padding-top: 9px;
  border-top: 1px solid var(--dlAlertSeparator);
  font-family: var(--dlFontFamilyNormal);
  font-size: 11.5px;
  line-height: 1.5;
  color: var(--dlThemeGray-400);
`;

/**
 * The episode tooltip shared by the Alerts panel and the Alerts list page (PDW-182/183): header "Alert
 * definition", the criteria as grey chips, and a footer with the episode's last change - date and actor,
 * labelled per displayed state. Hover on the alert name opens it. The chips are rendered by the product
 * through `renderCriteria`, from the raw filter data core never interprets; its content mounts only while
 * the tooltip is open, so a renderer may load its code lists lazily.
 */
export const AlertEpisodeTooltip: React.FC<Props> = (props) => {
  const {tooltip, displayedState, systemActorName, renderCriteria, children} = props;
  const intl = useIntl();
  const {formatDate} = useFormatters();
  const content = (
    <div data-name={'dl-alert-episode-tooltip'}>
      <span css={headerCss}>{intl.formatMessage({id: 'alert.episode.tooltip.title'})}</span>
      {renderCriteria ? (
        renderCriteria(tooltip.filterComponent, tooltip.filterData)
      ) : (
        <AlertGenericCriteriaChips filterComponent={tooltip.filterComponent} filterData={tooltip.filterData} />
      )}
      <span css={footerCss}>
        <span style={{display: 'block'}}>
          {intl.formatMessage({id: `alert.episode.tooltip.${displayedState}.date`})}{' '}
          {tooltip.lastChangeOn ? formatDate(tooltip.lastChangeOn) : ''}
        </span>
        <span style={{display: 'block'}}>
          {intl.formatMessage({id: `alert.episode.tooltip.${displayedState}.by`})} {tooltip.lastChangeBy || systemActorName}
        </span>
      </span>
    </div>
  );
  return (
    <TooltipWrapperCore tooltip={content} noPadding wrapperCss={wrapperCss} popperInputCss={popperCss} placement='bottom-start'>
      {children}
    </TooltipWrapperCore>
  );
};

AlertEpisodeTooltip.displayName = 'AlertEpisodeTooltip';
